import { useAuthContext } from "@contexts/auth";
import { useDashboardUIActions, useDashboardUIContext } from "@contexts/dashboardUI";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { isBuildingTrial } from "@utils/checkPermiossion";
import { formatNumberToCurrency, formatNumberToLocale } from "@utils/formatNumber";
import { loadStorage, saveStorage } from "@utils/localStorageManager";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useEffect, useMemo, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import { orderFinancialSeries } from "./financialSeriesOrder";
import {
  filterCashFlowExport,
  filterCashFlowGraphSeries,
  physicalFinancialLabel,
} from "./cashFlowPlanning";
import { alignAccumulated, alignToAxis } from "./monthlySeries";
import {
  filterReceivablesExport,
  filterReceivablesSeries,
  receivablesStateOrLegacy,
} from "./receivablesAccess";

// O rótulo também é a CHAVE de "série escondida" (`hiddenItens`), por isso vive numa
// constante: literal repetido é como o rótulo e a chave saem de sincronia.
const VARIATION_LABEL = "Variação Mensal (Recebidas + A receber - Comprometido)";
const ANTICIPATION_LABELS = Object.freeze({
  disbursement: "Desembolso com Antecipação",
  added: "Antecipação (Adicionada)",
  removed: "Antecipação (Retirada)",
});
const LEGACY_ANTICIPATION_LABELS = Object.freeze({
  "Desembolso com Adiantamento": ANTICIPATION_LABELS.disbursement,
  "Adiantamento (Valor adicionado)": ANTICIPATION_LABELS.added,
  "Adiantamento (Valor retirado)": ANTICIPATION_LABELS.removed,
});
const normalizeHiddenFinancialLabels = (items) => [...new Set(
  (Array.isArray(items) ? items : []).map(
    (item) => LEGACY_ANTICIPATION_LABELS[item] ?? item,
  ),
)];

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const colorArr = {
  allReceiv: "#1f1f1f",
  physical: "#ba52f2",
  paid: "#3acff7",
  toPay: "#fc9403",
  disbursement: "#6852F2",
  predicted: "#FFA07A",
  accumulatedDisbursement: "#FFD700",
  accumulatedPaid: "#FFD700",
  advancementTo: "#3CB371",
  advancementFrom: "#f27752",
  commited: "#2d8a77",
  variation: "#c2bb42",
  balance: '#6eddbc',
  toReceive: "#525252",
  received: "#b6b6b6",
}

const reduceAdvancements = (taken, tooltipItem, mod) => {
  const list = taken.byItem.filter(i => {
    return i.data.some(j => j[mod] === tooltipItem.label)
  })
  const matches = []
  list.forEach((i) => {
    i.data.forEach((j) => {
      if (j[mod] === tooltipItem.label) {
        matches.push({
          ...j,
          label: i.name
        })
      }
    })
  })
  let response = []
  const totalAdv = matches.reduce((a, b) => a + b.value, 0)
  const revMod = mod === 'from' ? 'to' : 'from'
  matches.forEach(i => response.push(`${i.label}: ${formatNumberToCurrency(i.value)}, ${mod !== 'from' ? 'de' : 'para'} ${i[revMod]}`))

  return { totalAdv, response }
}

const FinancialBarChart = () => {

  const { activeProject } = useProjectContext();
  const { paidValues = [], disbursements = [], toPayValues = [], receivedBills = [], toReceiveBills = [], takenFrom = { byItem: [], byMonth: [] }, takenTo = { byItem: [], byMonth: [] }, followUps = [], accumulatedDisbursement = [], accumulatedPaid = [], physicals = [], allBalances = [], hasPrevision, cashFlowPlanning } = activeProject;
  const receivablesState = receivablesStateOrLegacy(activeProject?.access?.receivables);
  const { barChartDate } = useDashboardUIContext()
  const { setBarChartDate, setIsLoading } = useDashboardUIActions()
  const { selectXlsxData, selectGraph } = useProjectActions()
  const { filterDates, selectedGraph, filterDocs } = useProjectContext()
  const [hiddenItens, setHiddenItens] = useState([])
  const getDatesBetween = () => {
    const dates = [];
    const startDate = new Date(filterDates.beginAt);
    const endDate = new Date(filterDates.endAt);

    startDate.setDate(1);
    endDate.setDate(1);
    startDate.setMonth(startDate.getMonth() + 1);
    endDate.setMonth(endDate.getMonth() + 1);

    while (startDate <= endDate) {
      const month = String(startDate.getMonth() + 1).padStart(2, '0');
      const year = startDate.getFullYear();
      dates.push(`${month}/${year}`);
      startDate.setMonth(startDate.getMonth() + 1);
    }

    return dates;
  };

  const allDates = getDatesBetween(filterDates.beginAt, filterDates.endAt);

  const { user: { configurations } } = useAuthContext();
  const { flux: fluxConfig = [] } = configurations?.dashboard?.graphs || {}
  const graphColors = configurations?.graphColors || colorArr
  const { projection: rawProjectionConfig = [], advancements: rawAdvancementsConfig = [], balance: rawBalanceConfig = [] } = configurations?.projection?.graphs || {}
  // The organization policy decides which planning curves belong on the projection chart.
  // Advancements and balance are not cash-flow planning charts: the policy may hide a
  // series there, never add one.
  const projectionConfig = filterCashFlowGraphSeries(rawProjectionConfig, cashFlowPlanning)
  const advancementsConfig = filterCashFlowGraphSeries(
    rawAdvancementsConfig,
    cashFlowPlanning,
    { ensurePlanningSeries: false },
  )
  const balanceConfig = filterCashFlowGraphSeries(
    rawBalanceConfig,
    cashFlowPlanning,
    { ensurePlanningSeries: false },
  )

  const graphSet = selectedGraph

  const followUpDate = (point) => `${point.date.split("-")[1].padStart(2, "0")}/${point.date.split("-")[0]}`
  const scheduleCurve = (field) => followUps.map((point) => ({
    value: Number.isFinite(point?.[field]) ? point[field] : undefined,
    date: followUpDate(point),
  }))
  const scheduled = scheduleCurve('predictedValue')
  const baseCurve = scheduleCurve('baselineValue')
  const measuredCurve = scheduleCurve('measuredValue')

  const filter = (array, filters) => {
    return array.map(({ value, date }) => {
      const toReduce = filters?.filter(j => j.date === date).reduce((a, b) => a + b.value, 0)
      let newVal = value - toReduce
      if (newVal > -1 && newVal < 1) newVal = 0;
      if (toReduce) {
        return { value: newVal, date }
      }
      return { value, date }
    })
  }

  const filteredPaid = filter(paidValues, filterDocs.paidData)
  const filteredToPay = filter(toPayValues, filterDocs.toPayData)

  let lastReceived = 0;
  let lastToReceive = 0;

  const allBillsRaw = allDates.map(i => {
    const receivedIdx = receivedBills.findIndex(j => j.date === i)
    const toReceivedIdx = toReceiveBills.findIndex(j => j.date === i)

    let currentReceived = receivedBills[receivedIdx]?.accumulated
    if (!currentReceived) {
      currentReceived = lastReceived
    }
    lastReceived = currentReceived

    let currentToReceive = toReceiveBills[toReceivedIdx]?.accumulated
    if (!currentToReceive) {
      currentToReceive = lastToReceive
    }
    lastToReceive = currentToReceive

    return { date: i, accumulated: (lastReceived ?? 0) + (lastToReceive ?? 0), value: (receivedBills[receivedIdx]?.value ?? 0) + (toReceiveBills[toReceivedIdx]?.value ?? 0) }
  })

  useEffect(() => {
    setIsLoading(true)
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 500)
    return () => clearTimeout(timer)
  }, [barChartDate])

  const [paidList, disbursementList, toPayList, allBillsList, takenFromList, takenToList, dates, physicalsList, allBalancesList] = useMemo(() => {

    const paidDates = filteredPaid.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator;
    }, [])

    const disbursementDates = disbursements.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator
    }, [])

    const toPayDates = filteredToPay.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator
    }, [])

    const allBillsDates = allBillsRaw.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator
    }, [])


    const takenFromDates = takenFrom.byMonth.reduce((accumulator, item) => {
      accumulator.push(item.from)
      return accumulator
    }, [])

    const takenToDates = takenTo.byMonth.reduce((accumulator, item) => {
      accumulator.push(item.to)
      return accumulator
    }, [])

    const allBalancesDates = allBalances.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator
    }, [])

    const physicalsDates = physicals.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator
    }, [])

    const followUpDates = followUps.map(followUpDate)

    const dates = [...paidDates, ...disbursementDates, ...toPayDates, ...allBillsDates, ...takenFromDates, ...takenToDates, ...allBalancesDates, ...physicalsDates, ...followUpDates]

      .filter((item, index, self) => index === self.findIndex((indexedItem) => indexedItem === item))
      .sort((current, next) => {
        const [currentMonth, currentYear] = current.split("/")
        const [nextMonth, nextYear] = next.split("/")
        if (currentYear !== nextYear) {
          return Number(currentYear) - Number(nextYear);
        } else {
          return Number(currentMonth) - Number(nextMonth);
        }
      })

    const { paidMap, disbursementMap, toPayMap, allBillsMap, takenFromMap, takenToMap, physicalsMap, allBalancesMap } = dates.reduce((acc, item) => {
      acc.paidMap.set(item, filteredPaid.find(paidItem => paidItem.date === item)?.value ?? 0)
      acc.disbursementMap.set(item, disbursements.find(disbursementItem => disbursementItem.date === item)?.value ?? 0)
      acc.toPayMap.set(item, filteredToPay.find(toPayItem => toPayItem.date === item)?.value ?? 0)
      acc.allBillsMap.set(item, allBillsRaw.find(billItem => billItem.date === item)?.value ?? 0)
      acc.takenFromMap.set(item, takenFrom.byMonth.find(takenItem => takenItem.from === item)?.value ?? 0)
      acc.takenToMap.set(item, takenTo.byMonth.find(takenItem => takenItem.to === item)?.value ?? 0)
      acc.physicalsMap.set(item, physicals.find(physicalsItem => physicalsItem.date === item)?.value ?? 0)
      acc.allBalancesMap.set(item, allBalances.find(balanceItem => balanceItem.date === item)?.value ?? 0)
      return acc;
    }, { paidMap: new Map(), disbursementMap: new Map(), toPayMap: new Map(), allBillsMap: new Map(), takenFromMap: new Map(), takenToMap: new Map(), physicalsMap: new Map(), allBalancesMap: new Map() })

    return [Array.from(paidMap.values()), Array.from(disbursementMap.values()), Array.from(toPayMap.values()), Array.from(allBillsMap.values()), Array.from(takenFromMap.values()), Array.from(takenToMap.values()), dates, Array.from(physicalsMap.values()), Array.from(allBalancesMap.values())]
  }, [filteredPaid, disbursements, filteredToPay, allBillsRaw, takenFrom, takenTo, physicals, allBalances, followUps])


  const onBarClick = (event, itemsClicked) => {
    if (!event.chart.tooltip.dataPoints) return
    if (itemsClicked) {
      const selectedDate = event.chart.tooltip.dataPoints[0]?.label
      if (selectedDate == barChartDate) {
        setBarChartDate();
      } else {
        setBarChartDate(selectedDate)
      }
    } else {
      setBarChartDate();
    }
  }

  // ⚠️ O eixo do gráfico é `dates` (é o que vai em `labels`), e cada série é
  // consumida POR ÍNDICE. Então toda série tem de ser alinhada a `dates` —
  // qualquer outra base desloca os pontos. Duas fontes de desalinhamento havia aqui:
  //
  //  1. séries montadas com `.map()` sobre o array de dados cru (`receivedBills`,
  //     `toReceiveBills`): em ordem de chegada e só com as competências que têm
  //     valor. Caso real da Prívia — R$ 206.800,00 em jun/2024 e R$ 187.095,71 em
  //     abr/2025 apareciam em abr/2024 e mai/2024, os dois primeiros índices;
  //  2. séries alinhadas a `allDates` (o range do filtro) enquanto o eixo é
  //     `dates` (união das competências com dado). `dates` ⊇ `allDates`, então a
  //     partir da primeira divergência tudo escorregava.
  //
  // `alignToAxis`/`alignAccumulated` (./monthlySeries) resolvem por competência.
  const accumulatedDisbursementList = alignAccumulated(dates, accumulatedDisbursement)
  const accumulatedPaidList = alignAccumulated(dates, accumulatedPaid)

  const scheduledList = alignToAxis(dates, scheduled, { fallback: undefined, aggregate: "last" })
  const baseList = alignToAxis(dates, baseCurve, { fallback: undefined, aggregate: "last" })
  const measuredList = alignToAxis(dates, measuredCurve, { fallback: undefined, aggregate: "last" })

  const buildMonthly = (valueField) => {
    const perFollowUp = followUps.map(i => ({
      value: Number.isFinite(i[valueField]) ? i[valueField] : undefined,
      date: `${i.date.split("-")[1].padStart(2, "0")}/${i.date.split("-")[0]}`,
    }))
    return alignToAxis(dates, perFollowUp, { fallback: undefined, aggregate: "last" })
  }
  const measuredMonthlyList = buildMonthly('measuredDeltaValue')

  const commitedList = dates.map((i, idx) => {
    return paidList[idx] + toPayList[idx]
  })

  const receivedList = alignToAxis(dates, receivedBills)
  const toReceiveList = alignToAxis(dates, toReceiveBills)

  const variationList = dates.map((i, idx) => {
    return (receivedList[idx] + toReceiveList[idx]) - commitedList[idx]
  })

  const disbWithAdvanceList = disbursementList.map((i, idx) => i + takenToList[idx] - takenFromList[idx])

  const checkGraphConfig = (i) => {
    switch (i) {
      case 'accumulatedDisbursement':
        return {
          label: 'Desembolso Acumulado',
          borderColor: graphColors.disbursement,
          backgroundColor: graphColors.disbursement,
          type: 'line',
          data: accumulatedDisbursementList,
          stack: 'disbursement',
          hidden: hiddenItens.includes('Desembolso Acumulado'),
        }
      case 'accumulatedPaid':
        return {
          label: 'Pago Acumulado',
          borderColor: graphColors.paid,
          backgroundColor: graphColors.paid,
          type: 'line',
          data: accumulatedPaidList,
          stack: 'paid',
          hidden: hiddenItens.includes('Pago Acumulado'),
        }
      case 'allBills':
        return {
          label: "Receber/Recebidas",
          borderColor: graphColors.allReceiv,
          backgroundColor: graphColors.allReceiv,
          data: allBillsList,
          type: 'line',
          hidden: hiddenItens.includes('Receber/Recebidas'),
        }
      case 'allBillsAccumulated':
        return {
          label: "Receber/Recebidas",
          borderColor: graphColors.allReceiv,
          backgroundColor: graphColors.allReceiv,
          data: allBillsRaw.map(i => {
            if (i.accumulated !== 0) {
              return i.accumulated
            }
            return undefined
          }),
          type: 'line',
          hidden: hiddenItens.includes('Receber/Recebidas'),
        }
      case 'allBalance':
        return {
          label: "Saldo de Caixa (Variação atual + Saldo anterior)",
          borderColor: graphColors.balance,
          backgroundColor: graphColors.balance,
          data: allBalancesList,
          stack: 'balance',
          type: 'line',
          hidden: hiddenItens.includes('Saldo de Caixa (Variação atual + Saldo anterior)'),
        }
      case 'commited':
        return {
          label: "Comprometido",
          borderColor: graphColors.commited,
          backgroundColor: graphColors.commited,
          data: commitedList,
          stack: 'commited',
          hidden: hiddenItens.includes('Comprometido'),
        }
      case 'disbursement':
        return {
          label: "Desembolso",
          borderColor: graphColors.disbursement,
          backgroundColor: graphColors.disbursement,
          data: disbursementList,
          stack: 'disbursement',
          hidden: hiddenItens.includes('Desembolso'),
        }
      case 'disbWithAdvance':
        return {
          label: ANTICIPATION_LABELS.disbursement,
          borderColor: graphColors.predicted,
          backgroundColor: graphColors.predicted,
          data: disbWithAdvanceList,
          stack: 'disbursementWithAdv',
          hidden: hiddenItens.includes(ANTICIPATION_LABELS.disbursement),
        }
      case 'paid':
        return {
          label: "Pago",
          borderColor: graphColors.paid,
          backgroundColor: graphColors.paid,
          data: paidList,
          stack: 'paid',
          hidden: hiddenItens.includes('Pago'),
        }
      case 'physicals':
        return {
          label: "Físico Financeiro",
          borderColor: graphColors.physical,
          backgroundColor: graphColors.physical,
          data: physicalsList,
          stack: 'physicals',
          hidden: hiddenItens.includes('Físico Financeiro'),
        }
      case 'received':
        return {
          label: "Recebidas",
          borderColor: graphColors.received,
          backgroundColor: graphColors.received,
          data: receivedList,
          stack: 'receive',
          type: 'bar',
          hidden: hiddenItens.includes('Recebidas'),
        }
      case 'scheduled':
        return {
          label: 'Previsto',
          borderColor: graphColors.predicted,
          backgroundColor: graphColors.predicted,
          type: 'line',
          data: scheduledList,
          stack: 'predicted',
          hidden: hiddenItens.includes('Previsto'),
        }
      case 'base':
        return {
          label: 'Linha de base',
          borderColor: '#FFD700',
          backgroundColor: '#FFD700',
          type: 'line',
          borderDash: [6, 4],
          data: baseList,
          stack: 'base',
          hidden: hiddenItens.includes('Linha de base'),
        }
      case 'measured':
        return {
          label: 'Medido acumulado',
          borderColor: '#16A34A',
          backgroundColor: '#16A34A',
          type: 'line',
          data: measuredList,
          stack: 'measured',
          hidden: hiddenItens.includes('Medido acumulado'),
        }
      case 'measuredMonthly':
        return {
          label: 'Medido mensal',
          borderColor: '#16A34A',
          backgroundColor: '#16A34A',
          data: measuredMonthlyList,
          stack: 'measuredMonthly',
          hidden: hiddenItens.includes('Medido mensal'),
        }
      case 'takenFrom':
        return {
          label: ANTICIPATION_LABELS.removed,
          borderColor: graphColors.advancementFrom,
          backgroundColor: graphColors.advancementFrom,
          data: takenFromList.map(i => i * -1),
          stack: 'disbursement',
          hidden: hiddenItens.includes(ANTICIPATION_LABELS.removed),
        }
      case 'takenTo':
        return {
          label: ANTICIPATION_LABELS.added,
          borderColor: graphColors.advancementTo,
          backgroundColor: graphColors.advancementTo,
          data: takenToList,
          stack: 'disbursement',
          hidden: hiddenItens.includes(ANTICIPATION_LABELS.added),
        }
      case 'toPay':
        return {
          label: "A pagar",
          borderColor: graphColors.toPay,
          backgroundColor: graphColors.toPay,
          data: toPayList,
          stack: 'paid',
          hidden: hiddenItens.includes('A pagar'),
        }
      case 'toReceive':
        return {
          label: "A Receber",
          borderColor: graphColors.toReceive,
          backgroundColor: graphColors.toReceive,
          data: toReceiveList,
          stack: 'receive',
          type: 'bar',
          hidden: hiddenItens.includes('A Receber'),
        }
      case 'variation':
        // O rótulo dizia "Recebidas - Comprometido", mas a fórmula sempre incluiu
        // `toReceive` nas entradas (ver `variationList`). O rótulo agora descreve
        // a conta que é feita de fato.
        return {
          label: VARIATION_LABEL,
          borderColor: graphColors.variation,
          backgroundColor: graphColors.variation,
          data: variationList,
          stack: 'variation',
          hidden: hiddenItens.includes(VARIATION_LABEL),
        }
      default:
        break;
    }
  }

  // Prevision buildings always show the monthly "Medido" bar in the projeção
  // chart by default, without a per-user toggle. Non-Prevision buildings never
  // get it here (so it never falls back to the Sienge measured on the chart).
  const graphs = {
    projectionGraph: filterReceivablesSeries(orderFinancialSeries(projectionConfig, { hasPrevision }), receivablesState).map(i => {
      return checkGraphConfig(i)
    }),
    fluxGraph: filterReceivablesSeries(fluxConfig, receivablesState).map(i => {
      return checkGraphConfig(i)
    }),
    advancementGraph: filterReceivablesSeries(orderFinancialSeries(advancementsConfig, { hasPrevision }), receivablesState).map(i => {
      return checkGraphConfig(i)
    }),
    balanceGraph: filterReceivablesSeries(balanceConfig, receivablesState).map(i => {
      return checkGraphConfig(i)
    }),
  }

  const handleLegendClick = (e, item) => {
    const { text } = item
    setHiddenItens(prev => {
      const updatedHiddenItems = prev.includes(text)
        ? prev.filter(i => i !== text)
        : [...prev, text];

      saveStorage('hiddenItens', updatedHiddenItems);
      return updatedHiddenItems;
    });
  }

  const graphOptions = {
    scales: {
      x: {
        stacked: true,
        display: true,
        title: {
          display: true,
          text: 'Mês/Ano',
          font: {
            weight: 'bold'
          }
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Valor',
          font: {
            weight: 'bold'
          }
        }
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    onClick: onBarClick,
    plugins: {
      legend: {
        position: "top",
        display: true,
        onClick: handleLegendClick
      },
      title: {
        display: false,
        text: "Projeção Financeira",
        align: "start",
        color: "black",
        font: {
          size: "18px"
        }
      },
      datalabels: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const dataset = tooltipItem.dataset;
            const index = tooltipItem.dataIndex;
            const label = tooltipItem.label
            if (tooltipItem.dataset.label === "Receber/Recebidas") {
              const toGet = graphSet !== 'fluxGraph' ? 'value' : 'accumulated'

              const toReceive = toReceiveBills.filter(i => i.date === label)[0]
              const toReceiveValue = toReceive !== undefined ? toReceive[toGet] : 0

              const received = receivedBills.filter(i => i.date === label)[0]
              const receivedValue = received !== undefined ? received[toGet] : 0

              return [`Total: ${formatNumberToLocale(allBillsRaw[index] ? allBillsRaw[index][toGet] : 0)}`, `Recebidas: ${formatNumberToLocale(receivedValue)}`, `A receber: ${formatNumberToLocale(toReceiveValue)}`];
            }
            if (tooltipItem.dataset.label === ANTICIPATION_LABELS.added) {
              const { totalAdv, response } = reduceAdvancements(takenTo, tooltipItem, 'to')
              return [`Total: ${formatNumberToLocale(totalAdv)}`, ...response]
            }
            if (tooltipItem.dataset.label === ANTICIPATION_LABELS.removed) {
              const { totalAdv, response } = reduceAdvancements(takenFrom, tooltipItem, 'from')
              return [`Total: ${formatNumberToLocale(totalAdv)}`, ...response]
            }
            if (tooltipItem.dataset.label === 'Desembolso') {
              if (takenFrom.byItem.filter(({ data }) => data.some(({ from }) => from === tooltipItem.label)).length > 0) {
                const { totalAdv } = reduceAdvancements(takenFrom, tooltipItem, 'from')
                return [`Desembolso: ${tooltipItem.formattedValue}`, `Desembolso - Antecipações: ${formatNumberToLocale(tooltipItem.raw - totalAdv)}`]
              }
              if (takenTo.byItem.filter(({ data }) => data.some(({ to }) => to === tooltipItem.label)).length > 0) {
                const { totalAdv } = reduceAdvancements(takenTo, tooltipItem, 'to')
                return [`Desembolso: ${tooltipItem.formattedValue}`, `Desembolso + Antecipações: ${formatNumberToLocale(tooltipItem.raw + totalAdv)}`]
              }
            }
            return `${dataset.label}: ${tooltipItem.formattedValue}`;
          }
        }
      }
    }
  }

  useEffect(() => {
    if (window.location.pathname.includes('projecao-financeira')) {
      selectGraph('advancementGraph')
    } else {
      selectGraph('fluxGraph')
    }
    setHiddenItens(normalizeHiddenFinancialLabels(loadStorage('hiddenItens')))
  }, [])

  useEffect(() => {
    let fields, resources;
    if (graphSet === 'balanceGraph') {
      fields = [
        { label: 'Data', key: 'date' },
        { label: 'Comprometido', key: 'commited' },
        { label: 'Variação Mensal', key: 'variation' },
        { label: 'Saldo de Caixa', key: 'allBalances' },
        { label: 'Recebidas', key: 'received' },
        { label: 'A Receber', key: 'toReceive' },
      ]
      resources = dates.map((date, index) => ({
        date,
        commited: commitedList[index],
        variation: variationList[index],
        allBalances: allBalancesList[index],
        received: receivedList[index],
        toReceive: toReceiveList[index],
      }));
    } else if (graphSet === 'fluxGraph') {
      fields = [
        { label: 'Data', key: 'date' },
        { label: 'Previsto', key: 'scheduled' },
        { label: 'Desembolso Acumulado', key: 'accumulatedDisbursement' },
        { label: 'Pago Acumulado', key: 'accumulatedPaid' },
        { label: 'Receber/Recebidas', key: 'allBillsRaw' }
      ]
      resources = dates.map((date, index) => ({
        date,
        scheduled: scheduledList[index],
        accumulatedDisbursement: accumulatedDisbursementList[index],
        accumulatedPaid: accumulatedPaidList[index],
        allBillsRaw: allBillsRaw.map(i => i.accumulated)[index],
      }));
    } else if (graphSet === 'projectionGraph') {
      fields = [
        { label: 'Data', key: 'date' },
        { label: 'Físico Financeiro', key: 'physicals' },
        { label: 'Desembolso', key: 'disbursement' },
        { label: ANTICIPATION_LABELS.disbursement, key: 'disbWithAdvance' },
        { label: 'Medido', key: 'measuredMonthly' },
        { label: 'Pago', key: 'paid' },
        { label: 'A pagar', key: 'toPay' },
        { label: 'Receber/Recebidas', key: 'allBillsRaw' },
      ];
      resources = dates.map((date, index) => ({
        date,
        physicals: physicalsList[index],
        disbursement: disbursementList[index],
        disbWithAdvance: disbWithAdvanceList[index],
        measuredMonthly: measuredMonthlyList[index],
        paid: paidList[index],
        toPay: toPayList[index],
        allBillsRaw: allBillsList[index],
      }));
    } else if (graphSet === 'advancementGraph') {
      fields = [
        { label: 'Data', key: 'date' },
        { label: 'Físico Financeiro', key: 'physicals' },
        { label: 'Desembolso', key: 'disbursement' },
        { label: ANTICIPATION_LABELS.disbursement, key: 'disbWithAdvance' },
        { label: 'Medido', key: 'measuredMonthly' },
        { label: 'Pago', key: 'paid' },
        { label: 'A pagar', key: 'toPay' },
        { label: 'Receber/Recebidas', key: 'allBillsRaw' },
        { label: ANTICIPATION_LABELS.added, key: 'takenTo' },
        { label: ANTICIPATION_LABELS.removed, key: 'takenFrom' },
      ];
      resources = dates.map((date, index) => ({
        date,
        physicals: physicalsList[index],
        disbursement: disbursementList[index],
        disbWithAdvance: disbWithAdvanceList[index],
        measuredMonthly: measuredMonthlyList[index],
        paid: paidList[index],
        toPay: toPayList[index],
        allBillsRaw: allBillsList[index],
        takenTo: takenToList[index],
        takenFrom: takenFromList[index],
      }));
    }

    ({ fields, resources } = filterReceivablesExport(
      { fields, resources },
      receivablesState,
    ));

    selectXlsxData({ fields, resources })
  }, [selectedGraph, receivablesState])


  if (graphSet === 'projectionGraph') {
    return <>
      <Bar options={graphOptions}
      data={{
        labels: dates,
        datasets: !isBuildingTrial() ? graphs.projectionGraph : [
          {
            label: "Desembolso",
            borderColor: "#6852F2",
            backgroundColor: "#6852F2",
            data: disbursementList,
            stack: 'disbursement'
          },

        ]
      }} />
    </>
  } else if (graphSet === 'advancementGraph') {
    return <>
      <Bar options={graphOptions}
      data={{
        labels: dates,
        datasets: !isBuildingTrial() ? graphs.advancementGraph : [
          {
            label: "Desembolso",
            borderColor: "#6852F2",
            backgroundColor: "#6852F2",
            data: disbursementList,
            stack: 'disbursement'
          },
        ]
      }} />
    </>
  } else if (graphSet === 'balanceGraph') {
    return <>
      <Bar options={graphOptions}
      data={{
        labels: dates,
        datasets: !isBuildingTrial() ? graphs.balanceGraph : [
          {
            label: "Desembolso",
            borderColor: "#6852F2",
            backgroundColor: "#6852F2",
            data: disbursementList,
            stack: 'disbursement'
          },
        ]
      }} />
    </>
  } else {
    return <>
      <Line options={graphOptions}
      data={{
        labels: dates,
        datasets: !isBuildingTrial() ? graphs.fluxGraph : [
          {
            label: "Desembolso",
            borderColor: "#6852F2",
            backgroundColor: "#6852F2",
            data: disbursementList,
            stack: 'disbursement'
          },

        ]
      }} />
    </>

  }
}

export default FinancialBarChart;
